import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listApiKeys, createApiKey, revokeApiKey } from '../api/client'
import PageHeader from '../components/PageHeader'
import Modal from '../components/Modal'

export default function ApiKeys() {
  const { t } = useTranslation(['settings', 'common'])
  const [keys, setKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [revealed, setRevealed] = useState(null) // { name, key } — shown once

  const load = async () => {
    setLoading(true)
    try { setKeys(await listApiKeys()) } catch (_) {}
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const handleRevoke = async (k) => {
    if (!confirm(t('apiKeys.revokeConfirm', { name: k.name }))) return
    try {
      await revokeApiKey(k.id)
      await load()
    } catch (e) {
      alert(e.response?.data?.detail || t('apiKeys.revokeFailed'))
    }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader
          title={t('apiKeys.title')}
          subtitle={t('apiKeys.subtitle')}
          actions={
            <button
              onClick={() => setCreating(true)}
              className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
            >
              {t('apiKeys.newButton')}
            </button>
          }
        />
      </div>

      <p className="text-xs text-gray-400 mb-4 max-w-2xl">{t('apiKeys.intro')}</p>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        {loading ? (
          <p className="text-sm text-gray-400 px-6 py-8">{t('apiKeys.loading')}</p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-gray-400 px-6 py-8">{t('apiKeys.empty')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('apiKeys.columns.name')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('apiKeys.columns.created')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('apiKeys.columns.expires')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('apiKeys.columns.lastUsed')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('apiKeys.columns.status')}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => {
                const revoked = !!k.revoked_at
                const expired = !revoked && k.expires_at && new Date(k.expires_at) < new Date()
                const status = revoked ? 'revoked' : expired ? 'expired' : 'active'
                const STATUS_CLS = {
                  active: 'bg-emerald-100 text-emerald-700',
                  expired: 'bg-amber-100 text-amber-700',
                  revoked: 'bg-gray-100 text-gray-500',
                }
                return (
                  <tr key={k.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors last:border-b-0">
                    <td className="px-5 py-3 font-medium text-gray-900">{k.name}</td>
                    <td className="px-5 py-3 text-gray-500">{new Date(k.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-gray-500">
                      {k.expires_at ? new Date(k.expires_at).toLocaleDateString() : t('apiKeys.neverExpires')}
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : t('apiKeys.neverUsed')}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[status]}`}>
                        {t(`apiKeys.status.${status}`)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {!revoked && (
                        <button onClick={() => handleRevoke(k)} className="text-xs text-gray-400 hover:text-red-500">
                          {t('apiKeys.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl">
        <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('apiKeys.howToUse.title')}</h2>
        <p className="text-xs text-gray-500 font-mono leading-relaxed">{t('apiKeys.howToUse.body')}</p>
      </div>

      {creating && (
        <CreateKeyModal
          t={t}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false)
            setRevealed({ name: created.name, key: created.key })
            load()
          }}
        />
      )}

      {revealed && <RevealKeyModal t={t} revealed={revealed} onClose={() => setRevealed(null)} />}
    </div>
  )
}

const EXPIRY_OPTIONS = [
  { value: '', labelKey: 'never' },
  { value: '30', labelKey: 'days30' },
  { value: '90', labelKey: 'days90' },
  { value: '365', labelKey: 'days365' },
]

function CreateKeyModal({ t, onClose, onCreated }) {
  const [name, setName] = useState('')
  const [expiresInDays, setExpiresInDays] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (!name.trim()) { setError(t('apiKeys.createModal.nameRequired')); return }
    setBusy(true)
    setError('')
    try {
      const created = await createApiKey(name.trim(), expiresInDays ? Number(expiresInDays) : null)
      onCreated(created)
    } catch (e) {
      setError(e.response?.data?.detail || t('apiKeys.createModal.createFailed'))
      setBusy(false)
    }
  }

  return (
    <Modal title={t('apiKeys.createModal.title')} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(420px, 80vw)' }}>
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('apiKeys.createModal.name')}</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit() }}
            placeholder={t('apiKeys.createModal.namePlaceholder')}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('apiKeys.createModal.expiresIn')}</label>
          <select
            value={expiresInDays}
            onChange={e => setExpiresInDays(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {EXPIRY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{t(`apiKeys.createModal.expiry.${o.labelKey}`)}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-3">
          <button
            onClick={submit}
            disabled={busy || !name.trim()}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
          >
            {busy ? t('apiKeys.createModal.creating') : t('apiKeys.createModal.create')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function RevealKeyModal({ t, revealed, onClose }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(revealed.key)
      setCopied(true)
    } catch (_) {}
  }

  return (
    <Modal title={t('apiKeys.reveal.title')} onClose={onClose} wide>
      <div className="space-y-4">
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
          {t('apiKeys.reveal.warning')}
        </p>
        <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono break-all select-all">
          {revealed.key}
        </code>
        <div className="flex gap-3">
          <button
            onClick={copy}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
          >
            {copied ? t('apiKeys.reveal.copied') : t('apiKeys.reveal.copy')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            {t('apiKeys.reveal.done')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
